import api from './index.js';
import { TourType } from '../types/onboarding';

/**
 * Maps frontend TourType enum to backend tour type names
 */
const TOUR_TYPE_MAP: Record<TourType, string> = {
  [TourType.FIRST_LOGIN]: 'getting_started',
  [TourType.OVERVIEW]: 'dashboard_overview',
  [TourType.CONVERSATIONS]: 'conversations',
  [TourType.SIDEBAR]: 'navigation_guide',
  [TourType.AGENTS]: 'agents_management',
  [TourType.SETTINGS]: 'settings_guide',
  [TourType.REPORTS]: 'reports_analytics',
  [TourType.RECORDS]: 'chat_records',
  [TourType.DOCUMENTS]: 'documents',
  [TourType.CALENDAR]: 'conversations',
  [TourType.SCHEDULER]: 'conversations',
  [TourType.LOGS]: 'chat_records',
  [TourType.TEAMS]: 'agents_management'
};

/**
 * Maps backend tour type names to frontend TourType enum
 */
const BACKEND_TOUR_MAP: Record<string, TourType> = {
  getting_started: TourType.FIRST_LOGIN,
  dashboard_overview: TourType.OVERVIEW,
  conversations: TourType.CONVERSATIONS,
  navigation_guide: TourType.SIDEBAR,
  agents_management: TourType.AGENTS,
  settings_guide: TourType.SETTINGS,
  reports_analytics: TourType.REPORTS,
  chat_records: TourType.RECORDS,
  documents: TourType.DOCUMENTS
};

export interface TourProgressResponse {
  tours: Record<string, boolean>;
  status: boolean;
  progress?: {
    total: number;
    completed: number;
    percentage: number;
  };
}

export interface CompleteTourResponse {
  tours: Record<string, boolean>;
  status: boolean;
  message: string;
}

/**
 * Complete a specific tour
 */
export async function completeTour(tourType: TourType): Promise<CompleteTourResponse> {
  const backendTourType = TOUR_TYPE_MAP[tourType];
  
  try {
    const { data } = await api.post('/agents/tours/complete', {
      tourType: backendTourType
    });
    return data;
  } catch (error) {
    console.error(`Failed to complete tour ${tourType}:`, error);
    throw error;
  }
}

/**
 * Fetch tour progress for all tours
 */
export async function getTourProgress(): Promise<TourProgressResponse> {
  try {
    const { data } = await api.get('/agents/tours/progress');
    return data;
  } catch (error) {
    console.error('Failed to fetch tour progress:', error);
    throw error;
  }
}

/**
 * Reset all tours
 */
export async function resetAllTours(): Promise<CompleteTourResponse> {
  try {
    const { data } = await api.post('/agents/tours/reset-all');
    return data;
  } catch (error) {
    console.error('Failed to reset all tours:', error);
    throw error;
  }
}

/**
 * Complete all tours
 */
export async function completeAllTours(): Promise<CompleteTourResponse> {
  try {
    const { data } = await api.post('/agents/tours/complete-all');
    return data;
  } catch (error) {
    console.error('Failed to complete all tours:', error);
    throw error;
  }
}

/**
 * Convert backend tour response to frontend tour types
 */
export function convertBackendToursToFrontend(
  backendTours: Record<string, boolean>
): TourType[] {
  return Object.entries(backendTours)
    .filter(([_, completed]) => completed)
    .map(([backendType, _]) => BACKEND_TOUR_MAP[backendType])
    .filter(Boolean) as TourType[];
}
